import React, { useEffect, useMemo, useRef, useState } from 'react';
import LocationMap from '../../LocationMap';
import { searchAddress, parcelByGushHelka, metersBetweenAddrParcel, pointToLatLng } from '../data/locate';

// Beyond this gap, an entered address and gush/chelka likely describe different
// properties — worth a heads-up before the analysis runs on the wrong parcel.
const MISMATCH_THRESHOLD_M = 150;

// Section 3 of the New Project form — property location. GovMap-backed address
// autocomplete + gush/chelka parcel lookup, both server-side (work on localhost),
// rendering a real static map with the parcel outline.
export default function PropertyLocator({
  locMode, setLocMode,
  city, setCity, street, setStreet, houseNo, setHouseNo,
  gush, setGush, helka, setHelka, subHelka, setSubHelka,
  onLocated,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [acLoading, setAcLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [addrPoint, setAddrPoint] = useState(null);   // EPSG:3857, from address pick
  const [parcelPoint, setParcelPoint] = useState(null); // EPSG:2039, from gush/chelka
  const [parcelGeom, setParcelGeom] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [mismatchDismissed, setMismatchDismissed] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  // Close the dropdown on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // On first mount with saved data (edit mode), locate so the map shows. Both an
  // address and a gush/chelka may be present — resolve each for the mismatch check.
  useEffect(() => {
    if (gush && helka) { locateParcel(true); }
    if (street && houseNo) {
      searchAddress(`${street} ${houseNo} ${city}`.trim())
        .then((rows) => { if (rows[0]?.coord) setAddrPoint(rows[0].coord); })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onStreetChange = (val) => {
    setStreet(val);
    setErr('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length < 2) { setSuggestions([]); setOpen(false); setAcLoading(false); return; }
    setAcLoading(true);
    setOpen(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const rows = await searchAddress(`${val} ${city}`.trim());
        setSuggestions(rows);
        setOpen(true);
      } catch {
        setSuggestions([]);
      } finally {
        setAcLoading(false);
      }
    }, 250);
  };

  const pickSuggestion = (s) => {
    if (s.street) setStreet(s.street);
    if (s.houseNo) setHouseNo(s.houseNo);
    if (s.city) setCity(s.city);
    setAddrPoint(s.coord || null);
    setMismatchDismissed(false);
    setOpen(false);
  };

  // Re-geocode the typed address (debounced) so manual edits to the house number
  // or city move the map too — not only picking an autocomplete suggestion.
  const geoRef = useRef(null);
  const geocodeFields = (str, num, cty) => {
    if (geoRef.current) clearTimeout(geoRef.current);
    if (!str.trim() || !num.trim()) return;
    geoRef.current = setTimeout(async () => {
      try {
        const rows = await searchAddress(`${str} ${num} ${cty}`.trim());
        if (rows[0]?.coord) { setAddrPoint(rows[0].coord); setMismatchDismissed(false); }
      } catch { /* keep the last located point */ }
    }, 450);
  };
  const onHouseNoChange = (val) => { setHouseNo(val); geocodeFields(street, val, city); };
  const onCityChange = (val) => { setCity(val); geocodeFields(street, houseNo, val); };

  async function locateParcel(silent = false) {
    if (!gush || !helka) { if (!silent) setErr('יש להזין גוש וחלקה.'); return; }
    if (!silent) setBusy(true);
    setErr('');
    try {
      const r = await parcelByGushHelka(gush, helka);
      if (!r) { if (!silent) setErr('לא נמצאה חלקה תואמת לגוש/חלקה שהוזנו.'); }
      else { setParcelGeom(r.geometry); setParcelPoint(r.center); setMismatchDismissed(false); }
    } catch {
      if (!silent) setErr('איתור החלקה נכשל. נסו שוב.');
    } finally {
      if (!silent) setBusy(false);
    }
  }

  // The map reflects the active input method.
  const showParcel = locMode === 'parcel';
  const mapCoord = showParcel ? parcelPoint : addrPoint;
  const mapGeom = showParcel ? parcelGeom : null;

  // Report the active located point (as lat/lng) up, so the project can capture
  // a Street View thumbnail for it at save time.
  useEffect(() => {
    if (onLocated) onLocated(pointToLatLng(mapCoord));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapCoord]);

  const mismatchMeters = useMemo(
    () => metersBetweenAddrParcel(addrPoint, parcelPoint),
    [addrPoint, parcelPoint],
  );
  const showMismatch =
    mismatchMeters != null && mismatchMeters > MISMATCH_THRESHOLD_M && !mismatchDismissed;
  const mismatchLabel = mismatchMeters >= 1000
    ? `${(mismatchMeters / 1000).toFixed(1)} ק״מ`
    : `${Math.round(mismatchMeters / 10) * 10} מ׳`;

  return (
    <div className="np-loc">
      <div className="np-loc-fields">
        <div className="np-toggle" role="group" aria-label="סוג איתור">
          <button type="button" className={locMode === 'address' ? 'is-active' : ''} onClick={() => setLocMode('address')}>כתובת</button>
          <button type="button" className={locMode === 'parcel' ? 'is-active' : ''} onClick={() => setLocMode('parcel')}>גוש / חלקה</button>
        </div>

        {locMode === 'address' ? (
          <div className="np-row np-row-3">
            <label className="np-field">
              <span className="np-field-label">עיר<i className="np-req">*</i></span>
              <input type="text" value={city} onChange={(e) => onCityChange(e.target.value)} placeholder="בחרו עיר" />
            </label>
            <label className="np-field np-field-ac" ref={boxRef}>
              <span className="np-field-label">רחוב<i className="np-req">*</i></span>
              <input
                type="text"
                value={street}
                onChange={(e) => onStreetChange(e.target.value)}
                onFocus={() => suggestions.length && setOpen(true)}
                placeholder="התחילו להקליד כתובת…"
                autoComplete="off"
              />
              {open && (
                <div className="np-ac-pop" role="listbox">
                  {acLoading && (
                    <div className="np-ac-loading"><span className="rp-spinner" /> מחפש כתובות…</div>
                  )}
                  {!acLoading && suggestions.length === 0 && (
                    <div className="np-ac-empty">לא נמצאו כתובות תואמות</div>
                  )}
                  {!acLoading && suggestions.map((s) => (
                    <button type="button" role="option" aria-selected={false} key={s.id} className="np-ac-item" onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}>
                      <i className="fi fi-rr-marker" />
                      <span>{s.text}</span>
                    </button>
                  ))}
                </div>
              )}
            </label>
            <label className="np-field">
              <span className="np-field-label">מספר בית<i className="np-req">*</i></span>
              <input type="text" value={houseNo} onChange={(e) => onHouseNoChange(e.target.value)} placeholder="מס׳" />
            </label>
          </div>
        ) : (
          <>
            <div className="np-row np-row-3">
              <label className="np-field">
                <span className="np-field-label">גוש<i className="np-req">*</i></span>
                <input type="text" value={gush} onChange={(e) => setGush(e.target.value)} placeholder="הזינו מספר גוש" />
              </label>
              <label className="np-field">
                <span className="np-field-label">חלקה<i className="np-req">*</i></span>
                <input type="text" value={helka} onChange={(e) => setHelka(e.target.value)} placeholder="הזינו מספר חלקה" />
              </label>
              <label className="np-field">
                <span className="np-field-label">תת-חלקה</span>
                <input type="text" value={subHelka} onChange={(e) => setSubHelka(e.target.value)} placeholder="הזינו תת-חלקה" />
              </label>
            </div>
            <button type="button" className="np-btn-pill np-locate" onClick={() => locateParcel(false)} disabled={busy}>
              <i className="fi fi-rr-marker" /> {busy ? 'מאתר…' : 'אתר את החלקה'}
            </button>
          </>
        )}
        {err && <div className="np-locate-err"><i className="fi fi-rr-exclamation" /> {err}</div>}

        {showMismatch && (
          <div className="np-loc-warn" role="alert">
            <i className="fi fi-rr-triangle-warning" />
            <span>הכתובת והגוש/חלקה מצביעים על מיקומים שונים (מרחק ~{mismatchLabel}). ודאו שהפרטים נכונים.</span>
            <button type="button" className="np-loc-warn-x" aria-label="התעלם" onClick={() => setMismatchDismissed(true)}>
              <i className="fi fi-rr-cross-small" />
            </button>
          </div>
        )}
      </div>

      <div className="np-map">
        {mapCoord ? (
          <LocationMap
            coordinates={mapCoord}
            geometry={mapGeom}
            showStreetView={false}
            showTitles={false}
            stacked
            flat
            useStatic
            zoomDelta={mapGeom ? -2 : 0}
          />
        ) : (
          <div className="np-map-empty">
            <i className="fi fi-rr-map-marker" />
            <span>{showParcel ? 'הזינו גוש/חלקה ולחצו «אתר את החלקה».' : 'בחרו כתובת מהרשימה כדי להציג את הנכס על המפה.'}</span>
          </div>
        )}
      </div>
    </div>
  );
}
